<template>
  <router-link
    :to="{
      name: 'station',
      params: { id: station.id },
    }"
  >
    <div class="relative w-full rounded-md bg-white">
      <div class="flex items-center gap-2 py-1 pl-3 pr-2 text-sm">
        <div class="basis-[40%]">
          <p class="mb-1 font-medium">{{ t('name') }}</p>
          <p>{{ getLocalizedField(station.info) }}</p>
        </div>

        <div class="basis-[60%]">
          <p class="mb-1 font-medium">{{ t('address') }}</p>
          <p>{{ `${station.street}, ${station.zip} ${station.city}` }}</p>
        </div>

        <DropdownDotted :buttons="dropDownItems" />
      </div>
    </div>
  </router-link>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { DropdownItem } from '@/components/DropdownDotted.vue';
import DropdownDotted from '@/components/DropdownDotted.vue';
import router from '@/router';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import type { Station } from '@/entities/station/station.entity';
import { Alert } from '@/services/alert.service';
import { useDeleteStation } from '@/queries/use-stations';

const props = defineProps<{
  station: Station;
}>();

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const { mutateAsync: removeStation } = useDeleteStation();

const deleteStation = async (stationId: string) => {
  const alertResult = await Alert.fire({
    titleText: t('reallyDeleteStationTitle'),
    text: t('reallyDeleteStationText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('deleteStation'),
    denyButtonText: t('dontDelete'),
  });
  if (alertResult.isConfirmed) {
    removeStation(stationId);
  }
};

const dropDownItems: DropdownItem[] = [
  {
    title: t('editStation'),
    onClick: () =>
      router.push({
        name: 'station',
        params: { id: props.station.id },
      }),
  },
  {
    title: t('deleteStation'),
    onClick: () => deleteStation(props.station.id),
  },
];
</script>

<i18n lang="json">
{
  "en": {
    "name": "Name",
    "address": "Address",
    "editStation": "Edit station",
    "deleteStation": "Delete station",
    "reallyDeleteStationTitle": "Really delete station?",
    "reallyDeleteStationText": "The station will be removed (soft deleted).",
    "dontDelete": "Don't delete"
  },
  "de": {
    "name": "Name",
    "address": "Adresse",
    "editStation": "Station bearbeiten",
    "deleteStation": "Station löschen",
    "reallyDeleteStationTitle": "Station wirklich löschen?",
    "reallyDeleteStationText": "Die Station wird entfernt (soft deleted).",
    "dontDelete": "Nicht löschen"
  },
  "es": {
    "name": "Nombre",
    "address": "Dirección",
    "editStation": "Editar estación",
    "deleteStation": "Eliminar estación",
    "reallyDeleteStationTitle": "¿Eliminar la estación?",
    "reallyDeleteStationText": "La estación se eliminará (borrado lógico).",
    "dontDelete": "No eliminar"
  }
}
</i18n>
